import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowDownToLine, AlertCircle, CheckCircle, Info, RefreshCw, ChevronDown, Clock, XCircle, Loader2, History, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useBalance } from '../contexts/BalanceContext';
import CryptoIcon from '../components/CryptoIcon';
import { getNetworkOptionsForCoin } from '../constants/networks';
import axios from 'axios';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { 
      icon: Clock, 
      color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
      label: 'Pending'
    },
    processing: { 
      icon: Loader2, 
      color: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
      label: 'Processing',
      animate: true
    },
    completed: { 
      icon: CheckCircle, 
      color: 'text-green-400 bg-green-500/20 border-green-500/30',
      label: 'Completed'
    },
    rejected: { 
      icon: XCircle, 
      color: 'text-red-400 bg-red-500/20 border-red-500/30',
      label: 'Rejected'
    },
    failed: { 
      icon: XCircle, 
      color: 'text-red-400 bg-red-500/20 border-red-500/30',
      label: 'Failed'
    }
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className={`w-3.5 h-3.5 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
};

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Withdraw = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { balances, refreshBalance, loading: balanceLoading } = useBalance();
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [memoTag, setMemoTag] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('');
  const [cryptos, setCryptos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [addressValid, setAddressValid] = useState(null);
  const [selectedCryptoInfo, setSelectedCryptoInfo] = useState(null);
  
  // Withdrawal history state
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);

  useEffect(() => {
    fetchCryptos();
    fetchWithdrawalHistory();
    refreshBalance();
  }, []);

  useEffect(() => {
    if (selectedCrypto && cryptos.length > 0) {
      const info = cryptos.find(c => c.code === selectedCrypto);
      setSelectedCryptoInfo(info);
      // Use canonical network options (BTC → BTC only; others → 6 standard networks)
      const options = getNetworkOptionsForCoin(selectedCrypto);
      setSelectedNetwork(options[0]);
    }
  }, [selectedCrypto, cryptos]);

  useEffect(() => {
    if (address && selectedCrypto) {
      validateAddress();
    }
  }, [address, selectedCrypto, memoTag]);

  const fetchCryptos = async () => {
    try {
      const token = localStorage.getItem('token');
      const cryptoResponse = await axios.get(`${API}/crypto/supported-currencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCryptos(cryptoResponse.data);
      if (cryptoResponse.data.length > 0) {
        setSelectedCrypto(cryptoResponse.data[0].code);
      }
    } catch (error) {
      console.error('Error fetching cryptos:', error);
      toast({ title: 'Error', description: 'Failed to load cryptocurrencies', variant: 'destructive' });
    }
  };

  const fetchWithdrawalHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/withdrawal-requests/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by date, newest first
      const sorted = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setWithdrawalHistory(sorted);
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
      // Don't show error toast for history, just log it
    } finally {
      setHistoryLoading(false);
    }
  };

  const validateAddress = async () => {
    if (!address || address.length < 10) {
      setAddressValid(null);
      return;
    }

    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/crypto/validate-address`,
        { address, currency: selectedCrypto, memo: memoTag },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddressValid(response.data.valid);
    } catch (error) {
      setAddressValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleWithdrawal = async () => {
    if (!address || !amount || !selectedCrypto) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (addressValid === false) {
      toast({ title: 'Error', description: 'Invalid wallet address', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        currency: selectedCrypto,
        amount: parseFloat(amount),
        address: address,
        network: selectedNetwork,
        memo_tag: memoTag || undefined
      };

      const response = await axios.post(`${API}/crypto/withdraw`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ 
        title: 'Success', 
        description: 'Withdrawal request submitted successfully! Track status below.' 
      });

      // Reset form
      setAmount('');
      setAddress('');
      setMemoTag('');
      setAddressValid(null);

      // Refresh balance and history
      refreshBalance();
      fetchWithdrawalHistory();
      
      // Make sure history section is visible
      setShowHistory(true);

    } catch (error) {
      let errorMessage = 'Withdrawal failed';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err.message).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-2">
            <ArrowDownToLine className="w-6 h-6 text-cyan-400" />
            <h1 className="text-2xl font-bold text-white">Withdraw</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Withdrawal Form */}
          <div className="space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-6 neon-border">
              <div className="flex justify-between items-center mb-2">
                <div className="text-cyan-600 text-sm">Available Balance</div>
                <button
                  onClick={refreshBalance}
                  disabled={balanceLoading}
                  className="p-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
                  title="Refresh balance"
                >
                  <RefreshCw className={`w-4 h-4 text-cyan-400 ${balanceLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="text-3xl font-bold text-cyan-400">
                {balances && selectedCrypto && balances[selectedCrypto.toUpperCase()] !== undefined
                  ? `${parseFloat(balances[selectedCrypto.toUpperCase()]).toFixed(8)} ${selectedCrypto.toUpperCase()}`
                  : `0.00000000 ${selectedCrypto ? selectedCrypto.toUpperCase() : 'USDT'}`}
              </div>
            </div>

            {/* Withdrawal Form */}
            <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-6">
              {/* Cryptocurrency Selection */}
              <div className="mb-6">
                <Label className="text-cyan-400 mb-3 block">Select Cryptocurrency</Label>
                <div className="relative">
                  <select
                    value={selectedCrypto}
                    onChange={(e) => setSelectedCrypto(e.target.value)}
                    className="w-full pl-14 pr-10 py-4 bg-slate-800/50 border border-cyan-500/30 text-cyan-100 rounded-lg focus:border-cyan-500 focus:outline-none appearance-none cursor-pointer"
                  >
                    {cryptos.map(crypto => {
                      const cryptoBalance = balances ? (balances[crypto.code.toUpperCase()] || 0) : 0;
                      return (
                        <option key={crypto.code} value={crypto.code}>
                          {crypto.icon} {crypto.name} ({crypto.code.toUpperCase()}) - {cryptoBalance > 0 ? parseFloat(cryptoBalance).toFixed(4) : '0.00'}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <CryptoIcon symbol={selectedCrypto} size="sm" />
                  </div>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400 pointer-events-none" />
                </div>
              </div>

              {/* Network Selection */}
              {selectedCrypto && (
                <div className="mb-4">
                  <Label className="text-cyan-400 mb-2 block">Network</Label>
                  <select
                    data-testid="withdraw-network-select"
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    className="w-full h-12 bg-slate-800/50 border border-cyan-500/30 text-cyan-100 rounded-lg px-3"
                  >
                    {getNetworkOptionsForCoin(selectedCrypto).map(network => (
                      <option key={network} value={network}>{network}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Wallet Address */}
              <div className="mb-4">
                <Label className="text-cyan-400 mb-2 block">Wallet Address</Label>
                <div className="relative">
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter recipient wallet address"
                    className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 pr-12"
                  />
                  {validating && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-cyan-400 border-t-transparent"></div>
                    </div>
                  )}
                  {!validating && addressValid === true && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
                  )}
                  {!validating && addressValid === false && (
                    <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
                  )}
                </div>
              </div>

              {/* Memo/Tag */}
              {selectedCryptoInfo && ['XRP', 'XLM', 'EOS'].includes(selectedCryptoInfo.code.toUpperCase()) && (
                <div className="mb-4">
                  <Label className="text-cyan-400 mb-2 block">Memo/Tag (if required)</Label>
                  <Input
                    value={memoTag}
                    onChange={(e) => setMemoTag(e.target.value)}
                    placeholder="Enter memo or destination tag"
                    className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100"
                  />
                </div>
              )}

              {/* Amount */}
              <div className="mb-4">
                <Label className="text-cyan-400 mb-2 block">Amount</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100"
                />
                <div className="flex gap-2 mt-2">
                  {[25, 50, 75, 100].map(percent => (
                    <button
                      key={percent}
                      onClick={() => {
                        if (balances && selectedCrypto) {
                          const balance = balances[selectedCrypto.toUpperCase()] || 0;
                          const calcAmount = (balance * percent / 100).toFixed(8);
                          setAmount(calcAmount);
                        }
                      }}
                      className="px-3 py-1 bg-slate-800 border border-cyan-500/30 rounded text-sm text-cyan-400 hover:border-cyan-500 transition-colors"
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Fee Info */}
              {selectedCryptoInfo && (
                <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-200">
                      <p className="font-semibold mb-1">Fee: {selectedCryptoInfo.fee} {selectedCryptoInfo.code.toUpperCase()}</p>
                      <p className="text-xs">You receive: {amount ? Math.max(0, parseFloat(amount) - parseFloat(selectedCryptoInfo.fee)).toFixed(8) : '0.00'} {selectedCryptoInfo.code.toUpperCase()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleWithdrawal}
                disabled={loading || !address || !amount || addressValid === false}
                className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : 'Submit Withdrawal'}
              </Button>
            </div>
          </div>

          {/* Right Column - Withdrawal History */}
          <div className="space-y-4">
            {/* History Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-semibold text-white">Withdrawal History</h2>
              </div>
              <button
                onClick={fetchWithdrawalHistory}
                disabled={historyLoading}
                className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
                title="Refresh history"
              >
                <RefreshCw className={`w-4 h-4 text-cyan-400 ${historyLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* History List */}
            <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl overflow-hidden">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                </div>
              ) : withdrawalHistory.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <ArrowDownToLine className="w-12 h-12 text-cyan-500/30 mx-auto mb-3" />
                  <p className="text-gray-400">No withdrawal requests yet</p>
                  <p className="text-sm text-gray-500 mt-1">Submit a withdrawal to see it here</p>
                </div>
              ) : (
                <div className="divide-y divide-cyan-500/10 max-h-[600px] overflow-y-auto">
                  {withdrawalHistory.map((withdrawal, index) => (
                    <div 
                      key={withdrawal.withdrawal_id || index}
                      className="p-4 hover:bg-slate-700/30 transition-colors"
                    >
                      {/* Top Row - Amount and Status */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CryptoIcon symbol={withdrawal.currency} size="sm" />
                          <span className="font-semibold text-white">
                            {parseFloat(withdrawal.amount || 0).toFixed(4)} {(withdrawal.currency || '').toUpperCase()}
                          </span>
                        </div>
                        <StatusBadge status={withdrawal.status} />
                      </div>
                      
                      {/* Address */}
                      <div className="mb-2">
                        <p className="text-xs text-gray-400 mb-0.5">To Address</p>
                        <p className="text-sm text-cyan-300 font-mono truncate" title={withdrawal.wallet_address}>
                          {withdrawal.wallet_address ? 
                            `${withdrawal.wallet_address.slice(0, 12)}...${withdrawal.wallet_address.slice(-8)}` 
                            : 'N/A'}
                        </p>
                      </div>
                      
                      {/* Details Row */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                          {formatDate(withdrawal.created_at)}
                        </span>
                        {withdrawal.network && (
                          <span className="text-gray-400 bg-slate-700/50 px-2 py-0.5 rounded">
                            {withdrawal.network}
                          </span>
                        )}
                      </div>
                      
                      {/* Fee and Amount After Fee */}
                      {withdrawal.fee && (
                        <div className="mt-2 pt-2 border-t border-slate-700/50 flex justify-between text-xs text-gray-400">
                          <span>Fee: {withdrawal.fee} {(withdrawal.currency || '').toUpperCase()}</span>
                          <span>Receive: {(parseFloat(withdrawal.amount || 0) - parseFloat(withdrawal.fee || 0)).toFixed(4)} {(withdrawal.currency || '').toUpperCase()}</span>
                        </div>
                      )}
                      
                      {/* Rejection reason if rejected */}
                      {withdrawal.status === 'rejected' && withdrawal.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-300">
                          <span className="font-medium">Reason:</span> {withdrawal.rejection_reason}
                        </div>
                      )}
                      
                      {/* Transaction hash if completed */}
                      {withdrawal.status === 'completed' && withdrawal.tx_hash && (
                        <div className="mt-2 flex items-center gap-1">
                          <span className="text-xs text-gray-400">TX:</span>
                          <a 
                            href={`https://tronscan.org/#/transaction/${withdrawal.tx_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                          >
                            {withdrawal.tx_hash.slice(0, 16)}...
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Summary */}
            {withdrawalHistory.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-400">
                    {withdrawalHistory.filter(w => w.status === 'pending' || w.status === 'processing').length}
                  </p>
                  <p className="text-xs text-yellow-500/70">Pending</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {withdrawalHistory.filter(w => w.status === 'completed').length}
                  </p>
                  <p className="text-xs text-green-500/70">Completed</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-400">
                    {withdrawalHistory.filter(w => w.status === 'rejected' || w.status === 'failed').length}
                  </p>
                  <p className="text-xs text-red-500/70">Rejected</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdraw;
